/* eslint-disable consistent-return */
import './styluses/main.styl'
import './styluses/fonts.css'

import '@formatjs/intl-datetimeformat/polyfill-force'
import '@formatjs/intl-datetimeformat/locale-data/ru'
import '@formatjs/intl-datetimeformat/locale-data/kk'
import '@formatjs/intl-datetimeformat/add-all-tz'

import Vue from 'vue'
import { initializeApp } from 'firebase/app'
import { getAnalytics } from 'firebase/analytics'
import { getRemoteConfig, fetchAndActivate } from 'firebase/remote-config'
import { getAuth } from 'firebase/auth'
import numeral from 'numeral'
import 'numeral/locales'
import vueMoment from 'vue-moment'
import moment from 'moment-timezone'
import 'moment/locale/ru'
import 'moment/locale/kk'
import { VueMaskDirective } from 'v-mask'
import JsonViewer from 'vue-json-viewer'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import store from './store'
import i18n from './i18n'
import rules from './utils/rules'
import { isDeepLink, getDeepLinkRoute } from './utils/deeplink'
import { initSentry } from './utils/sentry'

Vue.config.productionTip = false

initSentry({ Vue, router })

function initWebFirebase() {
  const firebaseConfig = JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG)
  const firebaseApp = initializeApp(firebaseConfig)
  store.dispatch('tools/setFirebaseApp', firebaseApp)
}

function initWebFirebaseAuth() {
  const auth = getAuth(store.state.tools.firebaseApp)
  auth.useDeviceLanguage()
  store.dispatch('auth/setFirebaseAuth', auth)
}

function initFirebaseAnalytics() {
  if (window.cordova) {
    window.FirebasePlugin.setAnalyticsCollectionEnabled(true)
    // Для Cordova, session_start отправляется автоматически SDK
    // при первом запуске FirebasePlugin
  } else {
    const analytics = getAnalytics(store.state.tools.firebaseApp)
    store.dispatch('analytics/setFirebaseAnalytics', analytics)
  }
}

function initRemoteConfig() {
  if (window.cordova) {
    const fetchTimeout = 60
    const minimumFetchInterval = 3600
    window.FirebasePlugin.setConfigSettings(fetchTimeout, minimumFetchInterval, () => {}, () => {})
    window.FirebasePlugin.fetchAndActivate(() => {}, () => {})
  } else {
    const remoteConfig = getRemoteConfig(store.state.tools.firebaseApp)
    remoteConfig.settings.minimumFetchIntervalMillis = 3600000
    fetchAndActivate(remoteConfig).then(() => {}).catch(() => {})
    store.dispatch('tools/setFirebaseRemoteConfig', remoteConfig)
  }
}

function configVue() {
  Vue.use(vueMoment, { moment })
  Vue.use(JsonViewer)
  Vue.use(VueDOMPurifyHTML)

  numeral.locale('en')
  numeral.localeData().delimiters.thousands = ' '
  numeral.fn._format = numeral.fn.format
  numeral.fn.format = function format(a, b) { return numeral.fn._format.call(this, a, b || numeral._.defaultRoundingFunction) }
  numeral._.defaultRoundingFunction = Math.floor
  Vue.filter('numeral', (value, format = '0,0') => numeral(value).format(format))
  Vue.filter('beautiful-minus', (value) => value.replace('-', '− '))
  Vue.filter('phone', (value) => (value && value.length === 12 ? value.replace(/[^0-9]/g, '').replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 ($2) $3-$4-$5') : value))

  Vue.directive('mask', VueMaskDirective)

  Vue.prototype.rules = rules

  Vue.prototype.$goBack = () => {
    window.history.length > 1 ? router.go(-1) : router.push('/kkm')
  }

  Vue.prototype.$isCordova = () => window.cordova !== undefined

  Vue.prototype.$openLink = (link, target) => {
    if (isDeepLink(link)) {
      return router.push(getDeepLinkRoute(link))
    }
    if (window.cordova !== undefined) {
      return window.cordova.InAppBrowser.open(link, target, 'hidenavigationbuttons=yes,hideurlbar=yes,zoom=no,toolbarcolor=#272727,closebuttoncolor=#ffffff')
    }
    return window.open(link, target)
  }

  Vue.prototype.$showHelpCrunch = () => {
    if (window.cordova !== undefined) {
      window.cordova.plugins.HelpCrunchPlugin.showChatScreen(() => {}, () => {})
    } else if (window.HelpCrunch) {
      window.HelpCrunch('showChatWidget')
      window.HelpCrunch('openChat')
    }
  }

  Vue.prototype.$isCordova = () => window.cordova !== undefined

  Vue.prototype.$isAndroid = () => window.cordova !== undefined && window.device.platform === 'Android'

  Vue.prototype.$isTestMode = () => import.meta.env.VITE_TEST === 'true'
}

function initWebApp() {
  initWebFirebase()
  initWebFirebaseAuth()

  // Инициализируем Firebase Analytics ДО создания Vue instance
  // чтобы session_start был первым событием
  initFirebaseAnalytics()
  initRemoteConfig()

  // Отправляем первое событие для триггера автоматического session_start
  // Это гарантирует правильную атрибуцию источников трафика
  store.dispatch('analytics/logEvent', {
    eventName: 'app_init',
    eventProperties: {},
  })

  const vue = new Vue({
    router,
    vuetify,
    store,
    i18n,
    render: (h) => h(App),

  })
  configVue()

  vue.$mount('#app')
}

initWebApp()
